import { FC, useCallback, useEffect, useRef, useState } from "react";

import { Summary, SummarySkeleton } from "@/components/questions/results";
import { ErrorWithRetry } from "@/components/ui";
import API from "@/hooks/api";
import type { Summary as SummaryData } from "@/types/resources";

interface RaceSummaryProps {
  figures: string;
  questionId: string;
  runId: string;
  studyId: string;
}

/** AI key insights written from `figures`; the server saves them per
    figures text. */
const RaceSummary: FC<RaceSummaryProps> = ({
  figures,
  questionId,
  runId,
  studyId,
}) => {
  const { error, generateSummary } = API.Studies.useGenerateRunSummary(
    studyId,
    runId
  );
  // By figures text; null when the request failed.
  const [results, setResults] = useState<
    Partial<Record<string, SummaryData | null>>
  >({});
  const requested = useRef(new Set<string>());

  const generate = useCallback(
    async (text: string) => {
      requested.current.add(text);
      const summary = await generateSummary({
        context: text,
        group_id: null,
        question_id: questionId,
      });
      setResults((current) => ({ ...current, [text]: summary }));
    },
    [generateSummary, questionId]
  );

  useEffect(() => {
    if (requested.current.has(figures)) return;
    // Deferred so the hook's state updates don't run synchronously inside
    // the effect.
    const timeout = setTimeout(() => void generate(figures), 0);
    return () => clearTimeout(timeout);
  }, [figures, generate]);

  const retry = () => {
    setResults((current) => ({ ...current, [figures]: undefined }));
    void generate(figures);
  };

  const result = results[figures];
  if (result === undefined) return <SummarySkeleton generating />;
  if (result === null || result.summary === null) {
    return (
      <ErrorWithRetry
        error={
          result === null
            ? (error ?? "The request failed.")
            : "The model returned no summary."
        }
        onRetry={retry}
        title="Couldn't write the key insights"
      />
    );
  }
  return <Summary summary={result} />;
};

export default RaceSummary;
